// Portal › Kullanıcılar — müşteri yöneticisi ekranı (M16 T20, K13; mockup 16, DR1/DR10, Δ1).
// Üstte "Danışmanınız" bilgi bandı (danışman listede DEĞİL), kullanıcı tablosu (ad · e-posta,
// set başına "düzenler: … · görüntüler: …", form daveti, durum rozeti DAVET BEKLİYOR / AKTİF /
// SÜRESİ DOLDU (+Yeniden gönder) / KAPALI), ayrı "+ Davet Et" düğmesi, satır → sağ panel
// (rol + üç durumlu kategori + gösterge istisnaları + işletmeler, form daveti ayrı düğme, Hesabı
// kapat). Firma = portal-store `activeClientId` (ClientShell seçicisi; sayfada ayrı seçici yok —
// TODO-KPI-57/11). Portal yetki v2: Süper Admin'de "Kullanıcılar | Yöneticiler" sekmeleri, kısıtlı
// Admin'de kapsam bandı ve kilitler. Kopyalar lib/client-admin-copy.ts TEK kaynak;
// müşteri yüzeyinde "AI/üretim/danışman notu" ifadesi yok. Sayfa sol dayalı, maxWidth 1400.
// Portal turu (2026-10-06): ≤1100 px'te liste KART düzeni (ImportRowsTable 1A deseni — matchMedia,
// yeni bağımlılık yok; kart/rozet/erişim özeti components/portal/ClientUserCard.tsx — ≤600 satır);
// kök landmark `<main>` (P3-26); mobilde dar dolgu + sağ panel alta iner.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ClientAdminProject,
  ClientAdminRow,
  ClientAdminSet,
  ClientEntity,
  ClientUser,
  ClientUsersResponse,
} from "@mahir/shared";
import { ApiError } from "../../api/client";
import {
  deactivateClientUser,
  listClientAdminProjects,
  listClientAdminSets,
  listClientUsers,
  listPortalClientAdmins,
  resendClientInvite,
} from "../../api/client-admin";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { ClientAdminsPanel } from "../../components/portal/ClientAdminsPanel";
import { ClientProjectInviteModal } from "../../components/portal/ClientProjectInviteModal";
import { ClientUserAccessDrawer } from "../../components/portal/ClientUserAccessDrawer";
import { ClientUserInviteModal } from "../../components/portal/ClientUserInviteModal";
import { ClientUserCards } from "../../components/portal/ClientUserCard";
import { ClientUserTable } from "../../components/portal/ClientUserTable";
import { CLIENT_ADMIN_COPY as C, CLIENT_ACCESS_ERROR_COPY as E } from "../../lib/client-admin-copy";
import { isScopedAdmin, scopeSummaryParts } from "../../lib/client-access-draft-v2";
import { scopeDimension } from "../../lib/client-access-errors";
import { applyScopeResult, SCOPE_LOADING, type ScopeLoad } from "../../lib/scope-load";
import { useMediaQuery } from "../../lib/use-media-query";
import { useAuthStore } from "../../stores/auth-store";
import { usePortalStore } from "../../stores/portal-store";

const CARD_BREAKPOINT = "(max-width: 1100px)";

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; data: ClientUsersResponse };

export function ClientUsers(): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const adminClientIds = useMemo(() => me?.adminClientIds ?? [], [me]);
  const activeClientId = usePortalStore((s) => s.activeClientId);
  const firmsStatus = usePortalStore((s) => s.firmsStatus);
  // Firma: kabuk seçicisindeki aktif firma (yönetici olduğu bir firmaysa), değilse ilk yönetilen firma.
  const clientId =
    firmsStatus === "idle" || firmsStatus === "loading"
      ? ""
      : activeClientId && adminClientIds.includes(activeClientId)
        ? activeClientId
        : (adminClientIds[0] ?? "");
  const membership = me?.adminClients?.find((m) => m.clientId === clientId) ?? null;
  const isSuper = membership?.role === "super_admin";
  const scope =
    membership?.role === "admin" && isScopedAdmin(membership.scope) ? membership.scope : null;
  const [tab, setTab] = useState<"users" | "admins">("users");
  const [admins, setAdmins] = useState<ClientAdminRow[] | null>(null);
  const [adminsError, setAdminsError] = useState(false);
  const [state, setState] = useState<LoadState>({ status: "loading" });
  // Yönetici paneli için: GEÇERLİ firmanın set/işletme listeleri yüklenene dek "loading".
  const [scopeLoad, setScopeLoad] = useState<ScopeLoad>(SCOPE_LOADING);
  const { sets, entities, status: scopeStatus } = scopeLoad;
  const loadSeq = useRef(0);
  const [projects, setProjects] = useState<ClientAdminProject[]>([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [projectInviteFor, setProjectInviteFor] = useState<ClientUser | null>(null);
  const [deactivateFor, setDeactivateFor] = useState<ClientUser | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});
  const [banner, setBanner] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const asCards = useMediaQuery(CARD_BREAKPOINT);
  const narrow = useMediaQuery("(max-width: 640px)");

  const load = useCallback(async (): Promise<void> => {
    if (!clientId) return;
    const seq = ++loadSeq.current;
    setState({ status: "loading" });
    // Önceki firmanın listeleri hemen düşer (başka firmanın set/işletmesiyle atama yapılmasın).
    setScopeLoad(SCOPE_LOADING);
    try {
      const [data, s, p] = await Promise.all([
        listClientUsers(clientId),
        listClientAdminSets(clientId)
          .then((r) => ({ ...r, failed: false }))
          .catch(() => ({
            sets: [] as ClientAdminSet[],
            entities: [] as ClientEntity[],
            hiddenEntityCount: 0,
            failed: true,
          })),
        listClientAdminProjects(clientId).catch(() => [] as ClientAdminProject[]),
      ]);
      if (seq !== loadSeq.current) return; // bu arada firma değişti / yeniden yüklendi
      setScopeLoad((cur) => applyScopeResult(cur, true, s));
      setProjects(p);
      setState({ status: "ready", data });
    } catch {
      if (seq !== loadSeq.current) return;
      setScopeLoad((cur) => applyScopeResult(cur, true, null));
      setState({ status: "error" });
    }
  }, [clientId]);

  useEffect(() => {
    setSelectedId(null);
    setTab("users");
    setAdmins(null);
    void load();
  }, [load]);

  const loadAdmins = useCallback(async (): Promise<void> => {
    if (!clientId || !isSuper) return;
    try {
      setAdmins(await listPortalClientAdmins(clientId));
      setAdminsError(false);
    } catch {
      setAdminsError(true);
    }
  }, [clientId, isSuper]);

  useEffect(() => {
    if (tab === "admins") void loadAdmins();
  }, [tab, loadAdmins]);

  /** Sakin yeniden yükleme (409 KPI_ACCESS_STALE): yükleme durumuna düşmeden güncel kullanıcılar. */
  async function refreshUsers(): Promise<void> {
    const data = await listClientUsers(clientId);
    setState({ status: "ready", data });
  }

  useEffect(() => {
    if (!banner) return;
    const t = window.setTimeout(() => setBanner(null), 6000);
    return () => window.clearTimeout(t);
  }, [banner]);

  const data = state.status === "ready" ? state.data : null;
  const users = data?.users ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    if (!q) return users;
    return users.filter(
      (u) =>
        u.fullName.toLocaleLowerCase("tr-TR").includes(q) ||
        u.email.toLocaleLowerCase("tr-TR").includes(q),
    );
  }, [users, search]);
  const selected = users.find((u) => u.id === selectedId) ?? null;
  const activeSuperAdminCount = data?.activeSuperAdminCount ?? 0;
  const selfIsLastSuper = isSuper && activeSuperAdminCount <= 1;

  function patchUser(updated: ClientUser): void {
    setState((prev) =>
      prev.status === "ready"
        ? {
            status: "ready",
            data: {
              ...prev.data,
              users: prev.data.users.some((u) => u.id === updated.id)
                ? prev.data.users.map((u) => (u.id === updated.id ? updated : u))
                : [...prev.data.users, updated],
            },
          }
        : prev,
    );
  }

  async function handleResend(u: ClientUser): Promise<void> {
    setRowBusy((b) => ({ ...b, [u.id]: true }));
    try {
      const r = await resendClientInvite(clientId, u.id);
      setBanner({
        kind: r.mailSent ? "ok" : "err",
        text: r.mailSent ? C.actions.resent : C.actions.resendFailed,
      });
      await load();
    } catch {
      setBanner({ kind: "err", text: C.actions.resendFailed });
    } finally {
      setRowBusy((b) => ({ ...b, [u.id]: false }));
    }
  }

  async function handleDeactivate(): Promise<void> {
    if (!deactivateFor) return;
    setDeactivating(true);
    try {
      await deactivateClientUser(clientId, deactivateFor.id);
      setDeactivateFor(null);
      setSelectedId(null);
      await load();
    } catch (err) {
      const dim = scopeDimension(err);
      setBanner({
        kind: "err",
        text:
          err instanceof ApiError && err.code === "CLIENT_ADMIN_LAST"
            ? C.deactivateDialog.lastAdmin
            : ((dim ? E.scope[dim] : undefined) ?? C.deactivateDialog.failed),
      });
      setDeactivateFor(null);
    } finally {
      setDeactivating(false);
    }
  }

  const clientName = data?.client.name ?? "";
  const scopeParts = scope ? scopeSummaryParts(scope, sets, entities) : null;

  const rowActions = (u: ClientUser, isSelected: boolean): JSX.Element => (
    <>
      {u.status === "invited" || u.status === "expired" ? (
        <button
          type="button"
          onClick={() => void handleResend(u)}
          disabled={rowBusy[u.id] === true}
          className="text-sm mr-3 disabled:opacity-50"
          style={{ color: "var(--color-primary)", fontWeight: 600, minHeight: 36 }}
        >
          {rowBusy[u.id] ? C.actions.resending : C.actions.resend}
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => setSelectedId(isSelected ? null : u.id)}
        aria-pressed={isSelected}
        className="text-sm"
        style={{ color: "var(--color-primary)", fontWeight: 600, minHeight: 36 }}
      >
        {C.actions.edit}
      </button>
    </>
  );

  return (
    <main
      style={{
        padding: narrow ? "20px 16px 32px" : "32px 40px 48px",
        maxWidth: 1400,
        fontFamily: "var(--font-sans)",
        minWidth: 0,
      }}
    >
      {/* Kicker + başlık */}
      <div
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--color-primary)",
          fontWeight: 700,
        }}
      >
        {clientName || C.kicker}
      </div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h1
          className="text-3xl mt-1"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
        >
          {C.title}
        </h1>
      </div>
      <p className="mt-2 text-sm" style={{ color: "var(--color-text-2)", maxWidth: 1100 }}>
        {C.subtitle}
      </p>

      {/* Danışman bandı */}
      <div
        className="mt-5 rounded-lg px-4 py-3 text-sm flex items-center justify-between gap-4 flex-wrap"
        style={{
          background: "color-mix(in srgb, var(--color-primary) 6%, white)",
          border: "1px solid color-mix(in srgb, var(--color-primary) 20%, white)",
          color: "var(--color-text)",
        }}
      >
        <span>
          {C.consultantBand.label}{" "}
          {data?.consultant ? (
            <>
              <strong>{data.consultant.fullName}</strong> · {data.consultant.email}{" "}
              {C.consultantBand.tail}
            </>
          ) : (
            <span style={{ color: "var(--color-text-3)" }}>{C.consultantBand.unknown}</span>
          )}
        </span>
        {selfIsLastSuper && data ? (
          <span style={{ color: "var(--color-text-3)" }}>{C.consultantBand.lastAdmin}</span>
        ) : null}
      </div>

      {scopeParts ? (
        <div
          data-testid="admin-scope-band"
          className="mt-3 rounded-lg px-4 py-2 text-sm"
          style={{
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
            border: "1px solid var(--color-warning-border)",
          }}
        >
          {C.admins.scopeBand(scopeParts.cats, scopeParts.ents)}
        </div>
      ) : null}

      {isSuper ? (
        <div role="tablist" aria-label={C.admins.tabsAria} className="mt-4 flex gap-1">
          {(["users", "admins"] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              id={`users-tab-${t}`}
              aria-controls={`users-panel-${t}`}
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{
                background:
                  tab === t ? "color-mix(in srgb, var(--color-primary) 10%, white)" : "transparent",
                color: tab === t ? "var(--color-primary)" : "var(--color-text-2)",
                fontWeight: 600,
              }}
            >
              {t === "users" ? C.admins.usersTab : C.admins.tab}
            </button>
          ))}
        </div>
      ) : null}

      {banner ? (
        <div
          role={banner.kind === "err" ? "alert" : "status"}
          className="mt-3 rounded-md px-3 py-2 text-sm"
          style={{
            background:
              banner.kind === "err"
                ? "color-mix(in srgb, var(--color-error) 10%, white)"
                : "color-mix(in srgb, var(--color-success) 12%, white)",
            color: banner.kind === "err" ? "var(--color-error)" : "var(--color-success)",
          }}
        >
          {banner.text}
        </div>
      ) : null}

      {isSuper && tab === "admins" ? (
        <div
          id="users-panel-admins"
          className="mt-4"
          role="tabpanel"
          aria-labelledby="users-tab-admins"
        >
          <ClientAdminsPanel
            clientId={clientId}
            admins={admins}
            loadError={adminsError}
            sets={sets}
            entities={entities}
            scopeStatus={scopeStatus}
            onRetryScope={() => void load()}
            onChanged={async () => {
              await Promise.all([loadAdmins(), refreshUsers().catch(() => undefined)]);
            }}
          />
        </div>
      ) : (
        <div
          id="users-panel-users"
          role={isSuper ? "tabpanel" : undefined}
          aria-labelledby={isSuper ? "users-tab-users" : undefined}
          className="mt-4 grid gap-5 items-start"
          style={{
            gridTemplateColumns: selected && !asCards ? "minmax(0, 1fr) 360px" : "minmax(0, 1fr)",
          }}
        >
          {/* Liste kartı */}
          <section
            className="rounded-xl"
            style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
          >
            <div className="flex items-center justify-between gap-3 p-4 flex-wrap">
              <input
                type="search"
                aria-label={C.search}
                placeholder={C.search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="p-2 rounded border text-sm"
                style={{ borderColor: "var(--color-border)", width: 320, maxWidth: "100%" }}
              />
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                disabled={!data}
                className="px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                style={{
                  background: "var(--color-primary)",
                  color: "var(--color-surface)",
                  fontWeight: 600,
                }}
              >
                {C.invite}
              </button>
            </div>

            {state.status === "loading" ? (
              <div className="px-4 pb-4 text-sm" style={{ color: "var(--color-text-3)" }}>
                {C.loading}
              </div>
            ) : state.status === "error" ? (
              <div
                className="px-4 pb-4 text-sm flex items-center gap-3"
                style={{ color: "var(--color-error)" }}
              >
                {C.loadError}
                <button type="button" onClick={() => void load()} className="underline">
                  {C.retry}
                </button>
              </div>
            ) : users.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <div className="text-base" style={{ color: "var(--color-text)", fontWeight: 600 }}>
                  {C.empty.title}
                </div>
                <div className="text-sm mt-1" style={{ color: "var(--color-text-3)" }}>
                  {C.empty.body}
                </div>
                <button
                  type="button"
                  onClick={() => setInviteOpen(true)}
                  className="mt-4 px-4 py-2 rounded-lg text-sm"
                  style={{
                    background: "var(--color-primary)",
                    color: "var(--color-surface)",
                    fontWeight: 600,
                  }}
                >
                  {C.empty.cta}
                </button>
              </div>
            ) : asCards ? (
              <ClientUserCards
                users={filtered}
                selectedId={selectedId}
                meId={me?.id ?? null}
                actions={rowActions}
              />
            ) : (
              <ClientUserTable
                users={filtered}
                selectedId={selectedId}
                meId={me?.id ?? null}
                actions={rowActions}
              />
            )}

            {data ? (
              <div
                className="flex items-center justify-between px-4 py-3 text-xs"
                style={{ color: "var(--color-text-3)" }}
              >
                <span>{C.footer.range(filtered.length)}</span>
                <span>{C.footer.note}</span>
              </div>
            ) : null}
          </section>

          {/* Sağ panel */}
          {selected ? (
            <ClientUserAccessDrawer
              key={selected.id}
              clientId={clientId}
              user={selected}
              sets={sets}
              entities={entities}
              scope={scope}
              isSelf={selected.id === me?.id}
              activeSuperAdminCount={activeSuperAdminCount}
              canEditManagers={isSuper}
              canInviteToProject={isSuper}
              onSaved={patchUser}
              onStale={refreshUsers}
              onInviteToProject={() => setProjectInviteFor(selected)}
              onDeactivate={() => setDeactivateFor(selected)}
              onClose={() => setSelectedId(null)}
            />
          ) : null}
        </div>
      )}

      <ClientUserInviteModal
        clientId={clientId}
        sets={sets}
        entities={entities}
        scope={scope}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(r) => {
          setInviteOpen(false);
          setBanner(
            r.mailSent || r.existingActive
              ? null
              : { kind: "err", text: C.inviteModal.mailWarning },
          );
          void load();
        }}
      />
      <ClientProjectInviteModal
        clientId={clientId}
        user={projectInviteFor}
        projects={projects}
        onClose={() => setProjectInviteFor(null)}
        onInvited={() => {
          setProjectInviteFor(null);
          setBanner({ kind: "ok", text: C.projectInvite.sent });
          void load();
        }}
      />
      <ConfirmDialog
        open={deactivateFor !== null}
        title={C.deactivateDialog.title}
        body={C.deactivateDialog.body(deactivateFor?.fullName ?? "")}
        confirmLabel={C.deactivateDialog.confirm}
        tone="danger"
        busy={deactivating}
        onConfirm={() => void handleDeactivate()}
        onCancel={() => setDeactivateFor(null)}
      />
    </main>
  );
}

export default ClientUsers;
